// PRC-031: transiciones con sentido entre pantallas (docs/pricing/32_SISTEMA_VISUAL.md).
// - Entrar en un detalle: el contenido entra desde la derecha.
// - «Volver» (o atrás del navegador): sale hacia la derecha y el origen entra desde la izquierda.
// - Cambiar de sección desde el menú: fundido breve con un ligero desplazamiento vertical.
// Solo se anima el contenido (el menú y la barra superior quedan fuera) y se
// desactiva si el sistema pide reducir el movimiento.
// PRC-044: sin hueco vacío entre pantallas. Antes, la pantalla que salía se
// desvanecía del todo y, mientras se construía la nueva, no quedaba nada pintado
// (unas décimas en negro: el parpadeo). Ahora la anterior sigue a la vista hasta
// que la nueva está lista, y la nueva entra con su animación de dirección.
import React, { useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigationType, useOutlet, type Location } from 'react-router-dom';
import { RETURN_PARAM } from '../navigation';

// Marca que pone el menú al navegar: cambiar de sección es un fundido, no «Volver».
export const MENU_NAVIGATION_STATE = { menuNavigation: true } as const;

type Direction = 'forward' | 'back' | 'section';

// Pantallas de detalle de Pricing (las que se abandonan con «Volver»).
const DETAIL_PREFIXES = [
  '/margin-center/customers/',
  '/margin-center/products/',
  '/margin-center/control-comercial',
  '/optimization/',
  '/optimization',
  '/batch-optimization',
  '/data-upload',
  '/data-quality',
  '/connections',
  '/retraining',
  '/historical-costs',
  '/enviar-archivos',
];

const isDetail = (pathname: string) =>
  DETAIL_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`));

const pathOf = (target: string | null | undefined) => (target ? target.split('?')[0].split('#')[0] : null);

const directionOf = (previous: Location | null, current: Location, navigationType: string): Direction => {
  if (!previous) return 'section';
  if ((current.state as { menuNavigation?: boolean } | null)?.menuNavigation) return 'section';
  if (navigationType === 'POP') return 'back';
  // Ir al origen que traía la pantalla anterior es «Volver».
  const previousOrigin = new URLSearchParams(previous.search).get(RETURN_PARAM)
    ?? (previous.state as { returnTo?: string } | null)?.returnTo;
  if (pathOf(previousOrigin) === current.pathname) return 'back';
  const from = isDetail(previous.pathname);
  const to = isDetail(current.pathname);
  if (!from && to) return 'forward';
  if (from && !to) return 'back';
  if (from && to) return 'forward';
  return 'section';
};

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

// La pantalla nueva entra ya medio visible (nunca desde negro), con la dirección
// de siempre: detalle desde la derecha, «Volver» desde la izquierda, sección con
// un ligero desplazamiento vertical. No hay animación de salida.
const variants = {
  initial: (dir: Direction) => (dir === 'section' ? { opacity: 0.5, y: 6, x: 0 } : { opacity: 0.5, x: dir === 'forward' ? 28 : -28, y: 0 }),
  animate: { opacity: 1, x: 0, y: 0, transition: { duration: 0.22, ease: EASE_OUT } },
};

const PageTransition: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  const reduceMotion = useReducedMotion();
  const previousRef = useRef<Location | null>(null);
  const directionRef = useRef<Direction>('section');
  // La primera pantalla al cargar la app no se anima.
  const firstRenderRef = useRef(true);
  const isFirstRender = firstRenderRef.current;
  firstRenderRef.current = false;

  // La dirección se calcula una vez por cambio de pantalla (no por cambio de filtros).
  if (previousRef.current?.pathname !== location.pathname) {
    directionRef.current = directionOf(previousRef.current, location, navigationType);
    previousRef.current = location;
  } else {
    previousRef.current = location;
  }

  if (reduceMotion) return <>{outlet}</>;

  const direction = directionRef.current;
  return (
    // Al cambiar de pantalla, React monta la nueva en el mismo paso en que quita la
    // anterior: hasta entonces se sigue viendo la anterior, sin hueco vacío.
    <motion.div
      key={location.pathname}
      custom={direction}
      variants={variants}
      initial={isFirstRender ? false : 'initial'}
      animate="animate"
      style={{ width: '100%' }}
    >
      {outlet}
    </motion.div>
  );
};

export default PageTransition;
